// src/services/operationsService.js
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';

const OPERATIONS_API_URL = `${NODE_BACKEND_API_URL}/operations`;

export const operationsService = {
    /**
     * Start a new daily operation
     * @param {Object} data - { initialCash, timestamp, notes }
     * @returns {Promise} Response with operation data
     */
    startDay: async (data) => {
        const response = await axios.post(`${OPERATIONS_API_URL}/start`, {
            initialCash: data.initialCash,
            openingNotes: data.openingNotes || null
        });
        return response.data;
    },

    /**
     * Get current operation status
     * @returns {Promise} Response with current operation or null
     */
    getCurrentOperation: async () => {
        const response = await axios.get(`${OPERATIONS_API_URL}/current`);
        return response.data;
    },

    /**
     * Record a cash withdrawal during the day
     * @param {Object} data - { amount, reason, authorizedBy, timestamp }
     * @returns {Promise} Response with withdrawal record
     */
    recordWithdrawal: async (data) => {
        const response = await axios.post(`${OPERATIONS_API_URL}/withdrawal`, {
            amount: data.amount,
            reason: data.reason,
            description: data.description || null
        });
        return response.data;
    },

    /**
     * Record a manual cash deposit (income) during the day
     * @param {Object} data - { amount, reason, description }
     * @returns {Promise} Response with deposit record
     */
    recordDeposit: async (data) => {
        const response = await axios.post(`${OPERATIONS_API_URL}/deposit`, {
            amount: data.amount,
            reason: data.reason,
            description: data.description || null
        });
        return response.data;
    },

    /**
     * Record a cash movement (expense/payment)
     * @param {Object} data - { type, amount, description, category, timestamp }
     * @returns {Promise} Response with movement record
     */
    recordMovement: async (data) => {
        const response = await axios.post(`${OPERATIONS_API_URL}/movement`, {
            type: data.type, // 'expense', 'payment', 'adjustment'
            amount: data.amount,
            description: data.description,
            category: data.category || null
        });
        return response.data;
    },

    /**
     * Get all movements for current operation
     * @param {number} operationId - Optional operation ID, defaults to current
     * @returns {Promise} Response with movements array
     */
    getMovements: async (operationId = null) => {
        const url = operationId 
            ? `${OPERATIONS_API_URL}/${operationId}/movements`
            : `${OPERATIONS_API_URL}/current/movements`;
        const response = await axios.get(url);
        return response.data;
    },

    /**
     * Get closing summary with backend-calculated expected cash
     * @returns {Promise} Response with calculated closing summary
     */
    getClosingSummary: async () => {
        const response = await axios.get(`${OPERATIONS_API_URL}/closing-summary`);
        return response.data;
    },

    /**
     * Close the day operation
     * @param {Object} data - { countedCash, closingNotes }
     * @returns {Promise} Response with closed operation and report ID
     */
    closeDay: async (data) => {
        const response = await axios.post(`${OPERATIONS_API_URL}/close`, {
            countedCash: data.countedCash,
            closingNotes: data.closingNotes || null
        });
        return response.data;
    },

    /**
     * Get operation report by date
     * @param {string} date - Date in YYYY-MM-DD format
     * @returns {Promise} Response with comprehensive report
     */
    getOperationReport: async (date) => {
        const response = await axios.get(`${OPERATIONS_API_URL}/report/${date}`);
        return response.data;
    },

    /**
     * Get historical operations (list)
     * @param {Object} params - { startDate, endDate, status, page, limit }
     * @returns {Promise} Response with paginated operations list
     */
    getOperations: async (params = {}) => {
        const response = await axios.get(`${OPERATIONS_API_URL}`, {
            params: {
                startDate: params.startDate || null,
                endDate: params.endDate || null,
                status: params.status || null, // 'open', 'closed', 'suspended'
                page: params.page || 1,
                limit: params.limit || 20
            }
        });
        return response.data;
    }
};

export default operationsService;
